import { useState, useEffect, useRef } from 'react';

/**
 * Возвращает значение с задержкой после последнего изменения.
 * @param value - исходное значение
 * @param delay - задержка в мс
 * @param flushKey - при изменении значение синхронизируется сразу (без ожидания), напр. при открытии модалки
 */
export function useDebouncedValue<T>(
  value: T,
  delay: number,
  flushKey?: string | number | boolean
): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Не инициализировать текущим flushKey — иначе после remount с тем же ключом смена не детектится */
  const prevFlushKeyRef = useRef<string | number | boolean | undefined>(undefined);
  const debouncedRef = useRef(debouncedValue);
  debouncedRef.current = debouncedValue;

  useEffect(() => {
    const flushKeyChanged =
      flushKey !== undefined && flushKey !== prevFlushKeyRef.current;

    if (flushKeyChanged) {
      prevFlushKeyRef.current = flushKey;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setDebouncedValue(value);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const prevDebounced = debouncedRef.current;
    const immediateStringFlush =
      typeof value === 'string' &&
      (value === '' ||
        (typeof prevDebounced === 'string' && value.length < prevDebounced.length));

    if (immediateStringFlush) {
      setDebouncedValue(value);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
      timeoutRef.current = null;
    }, delay);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay, flushKey]);

  return debouncedValue;
}
